// /app/frontend/src/pages/seo/IntentionalRelationshipsPage.jsx
// Focus: Anti-swiping / anti-hookup-culture; why slow dating wins.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  mobileAppSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  StepCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function IntentionalRelationshipsPage() {
  const path = '/dating-app-for-intentional-relationships';
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Dating App For Intentional Relationships', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Dating App For Intentional Relationships"
        description="Looking for more than casual dating? Discover meaningful relationships, family-building, and long-term connection through UnityLove."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Promise"
        headline="Built For Intentional Relationships"
        sub="For people who are tired of swiping, ghosting, and hookup culture. UnityLove is designed for the dating people who actually want to find someone — and stay."
        body="Slower. Deeper. Honest from the first message."
        primaryCta={{ label: 'Create Your Profile Free', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Why Verification Matters', to: '/verified-dating-app' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* The honest problem */}
        <section className="mb-12">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            The Dating App Trap
          </h2>
          <div className="space-y-4 text-slate-700 leading-relaxed">
            <p>
              Most dating apps are designed to keep you on them, not to help you leave. The longer you swipe, the more revenue they make. So the experience is optimized for endless options, dopamine, and a low bar for messaging — which means you spend years on the platform, the conversations stay shallow, and the real connection you came for keeps not happening.
            </p>
            <p>
              UnityLove is built around a different bet: a platform that genuinely helps you build a relationship is a platform you will eventually leave — and recommend to your friends from the other side. That changes every product decision underneath it.
            </p>
          </div>
        </section>

        {/* What intentional means */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6 text-center">
            What Intentional Dating Actually Looks Like
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="🐢"
              accent="emerald"
              title="Slower Pace, Better Outcomes"
              body="No infinite swipe stack. No pressure to make snap judgments on faces. The interface itself encourages reading profiles, thinking, and writing thoughtful first messages."
            />
            <FeatureCard
              icon="🪞"
              accent="rose"
              title="Real Profile Depth"
              body="Long-form prompts about values, vision for the future, family goals, and relationship style. Photos matter — but they are not the whole pitch."
            />
            <FeatureCard
              icon="🗣️"
              accent="sky"
              title="Conversations That Go Somewhere"
              body="In-app prompts, themed lounges, and video introductions help conversations move toward depth instead of dying after three messages."
            />
            <FeatureCard
              icon="🛡️"
              accent="violet"
              title="Verified Members"
              body="Identity verification through Stripe Identity reduces catfishing and bots. When you are talking to someone, you can be confident they are real."
            />
            <FeatureCard
              icon="🎯"
              accent="amber"
              title="Clear Intent Up Front"
              body="Members declare what they are looking for — long-term, family-building, ENM, marriage. No more guessing whether the person on the other end wants what you want."
            />
            <FeatureCard
              icon="💜"
              accent="rose"
              title="Designed To Be Outgrown"
              body="The healthiest measure of this app is the people who delete it because they found someone. That is the goal — not your engagement metric."
            />
          </div>
        </section>

        {/* Hookup culture comparison */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            How Intentional Dating Is Different
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm sm:text-base">
              <thead>
                <tr className="border-b border-slate-300">
                  <th className="py-3 pr-4 font-semibold text-slate-900">Most Dating Apps</th>
                  <th className="py-3 font-semibold text-slate-900">UnityLove</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {[
                  ['Endless swipe stack', 'Curated daily matches and themed discovery'],
                  ['Photos lead, profile barely matters', 'Long-form profile, values, family goals'],
                  ['Casual messaging, no expectation of depth', 'In-app prompts encourage substantive first messages'],
                  ['Verification optional and rare', 'Identity verification encouraged and visible'],
                  ['Designed to keep you swiping', 'Designed to help you find someone and leave'],
                  ['Casual hookups dominate the tone', 'Long-term, family-building, intentional connection'],
                ].map(([a, b]) => (
                  <tr key={a} className="border-b border-slate-200/60 last:border-0">
                    <td className="py-2.5 pr-4">{a}</td>
                    <td className="py-2.5">{b}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Who this is for */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-3">
            Who This Is For
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              People in their late twenties, thirties, forties, and beyond who have done the casual chapter and now want something with weight. People recently single after a long relationship who do not want to play hookup-culture games. People building plural families. People exploring ENM thoughtfully. People who want children — or do not — and want to find someone whose vision matches.
            </p>
            <p>
              If you came to dating apps for fun and casual nights, there are platforms designed for that. UnityLove is not it. If you came to actually find someone, this is a different experience by design.
            </p>
          </div>
        </section>

        {/* How to use it well */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            How To Use UnityLove Well
          </h2>
          <div className="grid gap-3">
            <StepCard n={1} title="Fill out your profile fully" body="Long-form prompts are not optional decoration — they are how people decide whether your match is worth pursuing. Specifics beat generics every time." />
            <StepCard n={2} title="Verify your identity" body="Verified members get noticed faster and treated better. It is one of the highest-leverage things you can do in your first week." />
            <StepCard n={3} title="Read profiles before messaging" body="The first message that references something specific from a profile gets a reply rate several times higher than a generic one." />
            <StepCard n={4} title="Move to video early" body="Five minutes on video reveals more than fifty messages. Use the in-app video lounge to skip the long text-only phase." />
            <StepCard n={5} title="Be honest about your timeline" body="If you want kids in two years, say so. If you want a slow path, say so. The people who match yours will find you faster when the signal is clear." />
          </div>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/verified-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Verified Dating App</p>
              <p className="text-sm text-slate-600 mt-1">How verification protects real connections.</p>
            </a>
            <a href="/black-love-dating-app" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Black Love Dating App</p>
              <p className="text-sm text-slate-600 mt-1">Centering culture and connection.</p>
            </a>
            <a href="/dating-as-a-couple" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Dating As A Couple</p>
              <p className="text-sm text-slate-600 mt-1">For couples dating together.</p>
            </a>
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">The foundations of intentional ENM.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
